const DB_NAME = 'dpop';
const STORE_NAME = 'keys';
const KEY_PAIR_ID = 'signing';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function idbRead<T>(key: string): Promise<T | undefined> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbWrite(key: string, value: unknown): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function readOrGenerateKeyPair(): Promise<CryptoKeyPair> {
  const existing = await idbRead<CryptoKeyPair>(KEY_PAIR_ID);
  if (existing && existing.privateKey && existing.publicKey) return existing;

  const pair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify'],
  );
  await idbWrite(KEY_PAIR_ID, pair);
  return pair;
}

// Web Lock prevents two tabs opened simultaneously from generating distinct keypairs;
// the second tab waits, then reads the committed key. Falls back to unlocked path
// where navigator.locks is unavailable (Safari < 15.4).
export async function getOrCreateKeyPair(): Promise<CryptoKeyPair> {
  if (typeof navigator?.locks?.request === 'function') {
    return navigator.locks.request('dpop-keypair-init', readOrGenerateKeyPair);
  }
  return readOrGenerateKeyPair();
}

export async function clearKeyPair(): Promise<void> {
  await idbDelete(KEY_PAIR_ID);
}

export async function exportPublicJwk(pair: CryptoKeyPair): Promise<{ kty: string; crv: string; x: string; y: string }> {
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) {
    throw new Error('Unexpected JWK shape from WebCrypto export');
  }
  return { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y };
}
